import { DynamicDictService } from './services/dynamic-dict.service';
import { MatSelect } from '@angular/material/select';
import { JsonDynamicComponentService } from './../json-dynamic-component.service';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { HttpMethods } from './../enums/http-method.enum';
import { Injectable } from '@angular/core';
import { get, has, size, template, templateSettings, isEqual } from 'lodash';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  retry,
  share,
  tap,
} from 'rxjs/operators';
import { Source } from '../enums/source.enum';
import { Config, RequestBody, UIControlValidators } from '../models/ui-config';
import {
  ValidatorFn,
  Validators,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { DynamicFormValidators } from '../enums/dynamic-form-validators.enum';
import { UiControlType } from '../enums/ui-control-type.enum';
import { CompareFuncService } from './services/compare-func.service';
import { POSSIBLE_STATIC_OPTIONS } from '../constants/lib-constants';
import { CellStyleParameters } from '../models/data-view-parameters';

@Injectable({
  providedIn: 'root',
})
export class CommonUtilsService {
  externalConfig: any;
  private previousDependentValueMap = new WeakMap<any, any>();

  constructor(
    private jsonDynamicComponentService: JsonDynamicComponentService,
    private http: HttpClient,
    private compareFuncService: CompareFuncService,
    public dynamicDictService: DynamicDictService,
  ) {}

  getHttpCall(
    method: HttpMethods,
    url: string,
    body?: any,
    options?: any,
  ): Observable<any> {
    switch (method) {
      case HttpMethods.Get:
        return this.http.get(url, options);
      case HttpMethods.Delete:
        return this.http.delete(url, options);
      case HttpMethods.Post:
        return this.http.post(url, body, options);
      case HttpMethods.Patch:
        return this.http.patch(url, body, options);
      case HttpMethods.Put:
        return this.http.put(url, body, options);
      default:
        return this.http.get(url, options);
    }
  }

  getInterpolatedValue(expr: string, value: any, rowContext?: any) {
    if (expr && value) {
      try {
        templateSettings.interpolate = /{{([\s\S]+?)}}/g;
        const compiled = template(expr);
        return compiled(this.getParamsSource(value, rowContext));
      } catch (_e) {
        expr = '';
      }
    }
    return expr;
  }

  getRequestBodyObject(
    requestBody: any,
    configValue: any,
    requestBodyStatic: RequestBody = {},
    rowContext?: any,
  ) {
    let body;
    if (requestBody) {
      const paramsSource = this.getParamsSource(configValue, rowContext);
      body = this.getRequestObject(requestBody, paramsSource);
    }
    if (typeof body === 'object') {
      return Object.assign({}, body, requestBodyStatic || {});
    } else {
      return body;
    }
  }

  private getRequestObject(requestObject: any, paramsSource: any, body?: any) {
    if (Array.isArray(requestObject)) {
      if (!body) {
        body = [];
      }
      this.getArrayRequest(requestObject, paramsSource, body);
    } else if (typeof requestObject === 'object') {
      if (!body) {
        body = {};
      }
      this.getObjectRequest(requestObject, paramsSource, body);
    } else {
      return this.getPlainRequest(requestObject, paramsSource);
    }
    return body;
  }

  private getArrayRequest(arrayRequest: any, paramsSource: any, body: any) {
    arrayRequest.forEach((request, _index) => {
      if (Array.isArray(request)) {
        const tempBody = [];
        body.push(tempBody);
        this.getArrayRequest(request, paramsSource, tempBody);
      } else if (typeof request === 'object') {
        const tempBody = {};
        body.push(tempBody);
        this.getObjectRequest(request, paramsSource, tempBody);
      } else {
        body.push(this.getPlainRequest(request, paramsSource));
      }
    });
  }

  private getObjectRequest(requestObject: any, paramsSource: any, body: any) {
    for (let request in requestObject) {
      const requestValue = requestObject[request];
      if (Array.isArray(requestValue)) {
        const tempBody = [];
        body[request] = tempBody;
        this.getArrayRequest(requestValue, paramsSource, tempBody);
      } else if (typeof requestValue === 'object') {
        const tempBody = {};
        body[request] = tempBody;
        this.getObjectRequest(requestValue, paramsSource, tempBody);
      } else {
        body[request] = this.getPlainRequest(requestValue, paramsSource);
      }
    }
  }

  private getPlainRequest(request: any, paramsSource: any) {
    // Preserve the native type when the entire value is a single mustache
    // expression (e.g. "{{PRICE_PLUS.BORROWING_SUBSET_COLUMN}}"). Lodash
    // template() string-coerces arrays/objects via Array.toString, so multi
    // selects would otherwise be serialized as "a,b" instead of ["a","b"].
    // Mixed templates ("{{a}}/{{b}}") still go through getInterpolatedValue.
    if (typeof request === 'string') {
      const typed = this.evaluateSingleExpression(request, paramsSource);
      if (typed !== undefined) {
        return typed;
      }
    }
    request = this.getInterpolatedValue(request, paramsSource);
    return has(paramsSource, request) ? get(paramsSource, request) : request;
  }

  private static readonly SINGLE_EXPR_RE = /^\s*\{\{([\s\S]+)\}\}\s*$/;
  private static readonly VALID_IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

  /**
   * Evaluates a single `{{...}}` expression to its native type; other values
   * pass through unchanged. Lets config fields like `isMultiSelect` be a
   * dynamic expression instead of a static boolean.
   */
  resolveTypedConfigValue<T = unknown>(
    value: unknown,
    formValue: any = {},
    rowContext?: any,
  ): T {
    if (typeof value !== 'string') {
      return value as T;
    }
    const paramsSource = this.getParamsSource(formValue, rowContext);
    const typed = this.evaluateSingleExpression(value, paramsSource);
    return (typed === undefined ? value : typed) as T;
  }

  /**
   * Returns the native result of a value that is a single `{{...}}` expression,
   * or `undefined` when the value is not a single expression or eval fails.
   * Falling back to `undefined` lets the caller use the existing lodash path,
   * which also returns `''` on errors — so behavior for failing expressions is
   * unchanged.
   */
  private evaluateSingleExpression(
    request: string,
    paramsSource: any,
  ): unknown {
    const match = CommonUtilsService.SINGLE_EXPR_RE.exec(request);
    if (!match) {
      return undefined;
    }
    const inner = match[1];
    // Bail on nested mustache markers — those are mixed templates that should
    // stay on the string-coercion path.
    if (inner.includes('{{') || inner.includes('}}')) {
      return undefined;
    }
    try {
      const params = paramsSource || {};
      const keys = Object.keys(params).filter((k) =>
        CommonUtilsService.VALID_IDENT_RE.test(k),
      );
      const fn = new Function(...keys, `"use strict"; return (${inner});`);
      const value = fn(...keys.map((k) => params[k]));
      return value === undefined ? undefined : value;
    } catch (error) {
      console.warn('Failed to evaluate typed expression:', inner, error);
      return undefined;
    }
  }

  private getParamsSource(configValue: any, rowContext?: any) {
    return Object.assign(
      {},
      this.jsonDynamicComponentService?.externalParams || {},
      this.externalConfig || {},
      configValue,
      this.jsonDynamicComponentService.mainFormGroup?.value || {},
      {
        __key: this.dynamicDictService.key || configValue?.__key,
      },
      rowContext || {},
    );
  }

  safeEvaluate(expression: string): any[] {
    try {
      return new Function('return ' + expression)();
    } catch (error) {
      console.warn('Failed to evaluate expression:', expression, error);
      return [];
    }
  }

  private extractFirstDependency(sourceExpression: string): string | null {
    const match = sourceExpression.match(/{{\s*([\w]+)\?/);
    return match ? match[1] : null;
  }

  async getDropdownValues(
    config: Config,
    formValue: any,
    matSelect?: MatSelect,
    hasModelChanged?: boolean,
    rowContext?: any,
  ) {
    return new Promise((res, _rej) => {
      if (config.source === Source.Function) {
        config.loading = true;
        let values = [
          ...new Set([
            ...(config.valuesToAdd || []),
            ...this.safeEvaluate(
              this.getInterpolatedValue(
                config.valuesFunction,
                formValue,
                rowContext,
              ),
            ),
          ]),
        ];
        this.updateDropdownValuesBasedOnExpression(values, config, formValue);
        if (config?.order) {
          values = this.maintainOrder(values, config);
        } else {
          values = this.reorderDropdownOptions(values);
        }
        config.values = values;
        config.allOptions = values;
        const minNumOfOptionsToShow = Math.min(
          config.minNumOfOptionsToShow,
          config.allOptions.length,
        );
        config.optionsToShow = config.allOptions.slice(
          0,
          minNumOfOptionsToShow,
        );
        config.loading = false;
        res(true);
      } else if (
        config.source === Source.Url &&
        (config?.values == undefined ||
          (size(config?.values) >= 0 && config?.reloadValues) ||
          hasModelChanged)
      ) {
        config.loading = true;
        const url = this.getInterpolatedValue(
          config.valuesUrl,
          formValue,
          rowContext,
        );
        const body = this.getRequestBodyObject(
          config.requestBody,
          formValue,
          config.requestBodyStatic,
          rowContext,
        );
        this.getHttpCall(config.fetchMethod, url, body)
          .pipe(
            retry(2),
            debounceTime(500),
            distinctUntilChanged(),
            share(),
            tap((valuesResult) => {
              let values: any[] =
                (config.valueKeyPath
                  ? get(valuesResult, config.valueKeyPath)
                  : valuesResult) || [];
              if (config.mapValueNames) {
                values = this.updateValuesBasedOnMap(
                  values,
                  config.mapValueNames,
                  config.filterValues,
                );
              }
              config.loading = false;
              if (
                size(config.valuesToAdd) > 0 &&
                config.valuesToAdd.includes('CREATE NEW') &&
                matSelect?.value != undefined &&
                matSelect?.value !== 'CREATE NEW'
              ) {
                values = [
                  ...new Set([
                    ...values,
                    ...(config.valuesToAdd || []),
                    matSelect?.value.toLowerCase(),
                  ]),
                ];
              } else {
                values = [
                  ...new Set([...values, ...(config.valuesToAdd || [])]),
                ];
              }
              if (config?.isGrouped) {
                values = values.map(
                  (group: { label: string; options: any[] }) => ({
                    label: group.label,
                    options: group.options.filter(
                      (option) =>
                        option[config.optionDisplayFields[0]] !==
                        this.jsonDynamicComponentService.externalParams
                          ?.model_name,
                    ),
                  }),
                );
              }
              this.updateDropdownValuesBasedOnExpression(
                values,
                config,
                formValue,
              );
              if (config?.order) {
                values = this.maintainOrder(values, config);
              } else {
                values = this.reorderDropdownOptions(values);
              }
              config.values = values;
              config.allOptions = values;
              const minNumOfOptionsToShow = Math.min(
                config.minNumOfOptionsToShow || Infinity,
                config.allOptions.length,
              );
              config.optionsToShow = config.allOptions.slice(
                0,
                minNumOfOptionsToShow,
              );
              res(true);
              CommonUtilsService.adjustMatSelectPanel();
            }),
            catchError((_err) => {
              config.loading = false;
              res(true);
              return of();
            }),
          )
          .subscribe();
      } else if (config.source === Source.Dependent) {
        const exprValue = this.getInterpolatedValue(
          config.sourceExpression,
          formValue,
        );
        let values = exprValue.split(',');
        if (config?.specialValuesToAdd) {
          const conditions = Object.keys(config.specialValuesToAdd);

          for (const condition of conditions) {
            const isString =
              typeof config.specialValuesToAdd[condition] === 'string';

            if (condition === 'suffix' || condition === 'prefix') {
              const specialConditionalValues = isString
                ? [config.specialValuesToAdd[condition]]
                : config.specialValuesToAdd[condition];

              values.forEach((val, index, arr) => {
                for (const subString of specialConditionalValues) {
                  let newValue = '';
                  if (condition === 'suffix' && val.endsWith(subString))
                    newValue = val.split(subString)[0];
                  else if (condition === 'prefix' && val.startsWith(subString))
                    newValue = val.split(subString)[1];
                  if (newValue && !arr.includes(newValue)) arr.push(newValue);
                }
              });
            }
          }
        }
        this.updateDropdownValuesBasedOnExpression(values, config, formValue);
        if (config?.order) {
          values = this.maintainOrder(values, config);
        } else {
          values = this.reorderDropdownOptions(values);
        }
        config.values = values;
        config.allOptions = values;

        if (
          config?.dependentValues &&
          config?.sourceExpression &&
          config?.valuesToAdd
        ) {
          // Auto-sets the value of a dependent dropdown based on the parent dropdown's value.
          // Allows manual editing as well; only updates when parent value changes or target is empty.

          config.values = [...(config.valuesToAdd || [])];
          config.allOptions = [...(config.valuesToAdd || [])];

          const dependentProp = this.extractFirstDependency(
            config.sourceExpression,
          );
          const targetProp = config.property;
          const currentDependentVal = formValue?.[dependentProp];
          const previousDependentVal =
            this.previousDependentValueMap.get(config);

          const exprValue = this.getInterpolatedValue(
            config.sourceExpression,
            formValue,
          );
          const derivedValue = config.dependentValues?.[exprValue] ?? null;
          const targetValue = formValue?.[targetProp];

          if (
            targetValue == null ||
            (previousDependentVal != null &&
              currentDependentVal !== previousDependentVal)
          ) {
            config.control.setValue(derivedValue, { emitEvent: true });
            this.previousDependentValueMap.set(config, currentDependentVal);
          } else if (this.previousDependentValueMap.has(config)) {
            config.control.setValue(targetValue);
          } else {
            this.previousDependentValueMap.set(config, currentDependentVal);
          }
        }

        const minNumOfOptionsToShow = Math.min(
          config.minNumOfOptionsToShow,
          config.allOptions.length,
        );
        config.optionsToShow = config.allOptions.slice(
          0,
          minNumOfOptionsToShow,
        );
        res(true);
      } else {
        let values = [...(config.values || [])];
        if (config?.valuesToAdd) {
          if (typeof config.valuesToAdd === 'string') {
            // config.valuesToAdd is expected to be an expression
            values = [
              ...new Set([
                ...values,
                ...this.safeEvaluate(
                  this.getInterpolatedValue(
                    config?.valuesToAdd || '[]',
                    formValue,
                  ),
                ),
              ]),
            ];
          } else if (Array.isArray(config.valuesToAdd)) {
            values = [...new Set([...values, ...config.valuesToAdd])];
          } else {
            throw new Error('valuesToAdd should be an array or a string');
          }
        }
        if (config?.order) {
          values = this.maintainOrder(values, config);
        } else {
          values = this.reorderDropdownOptions(values);
        }
        config.loading = false;
        config.allOptions = values;
        const minNumOfOptionsToShow = Math.min(
          config.minNumOfOptionsToShow,
          config.allOptions.length,
        );
        config.optionsToShow = config.allOptions.slice(
          0,
          minNumOfOptionsToShow,
        );
        this.updateDropdownValuesBasedOnExpression(
          config.values,
          config,
          formValue,
        );
        res(true);
      }
    });
  }

  private maintainOrder(values: any[], config: Config): any[] {
    let currentSetValues = [];
    if (config?.control?.value) {
      currentSetValues =
        typeof config.control.value === 'string'
          ? [config.control.value]
          : [...config.control.value];
    }
    const currentSetValuesSet = new Set(currentSetValues);
    const newValues = values.filter((value) => !currentSetValuesSet.has(value));
    const orderedValues = [...currentSetValues, ...newValues];

    return orderedValues;
  }

  private reorderDropdownOptions(values: any[]) {
    values = [...values]?.sort(this.compareFuncService.compareValues);
    POSSIBLE_STATIC_OPTIONS.forEach((opt) => {
      const idx = values.indexOf(opt);
      if (idx !== -1) {
        values.unshift(...values.splice(idx, 1));
      }
    });
    return values;
  }

  private updateValuesBasedOnMap(
    values: any[],
    mapValuesObj: { [key: string]: string },
    filterValues: boolean,
  ) {
    let returnValues = [];
    for (let i = 0; i < values.length; i++) {
      if (values[i] in mapValuesObj) {
        returnValues.push(mapValuesObj[values[i]]);
      } else if (!filterValues) {
        returnValues.push(values[i]);
      }
    }
    return returnValues;
  }

  private updateDropdownValuesBasedOnExpression(
    values: any[],
    config: Config,
    formValue: any,
  ) {
    if (config.idExpressionUpdated && config.labelExpressionUpdated) {
      return;
    }
    if (
      config.dropdownID &&
      config.dropdownIdExpression &&
      !config.idExpressionUpdated
    ) {
      values.forEach((rec) => {
        rec[config.dropdownID] = this.getRequestBodyObject(
          config.dropdownIdExpression,
          { ...formValue, ...rec },
        );
      });
      config.idExpressionUpdated = true;
    }
    if (
      config.dropdownLabel &&
      config.dropdownLabelExpression &&
      !config.labelExpressionUpdated
    ) {
      values.forEach((rec) => {
        rec[config.dropdownLabel] = this.getInterpolatedValue(
          config.dropdownLabelExpression,
          { ...formValue, ...rec },
        );
      });
      config.labelExpressionUpdated = true;
    }
  }

  static createGUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  static adjustMatSelectPanel() {
    const matSelectPanel = document.getElementsByClassName(
      'mat-mdc-select-panel',
    )[0];
    if (matSelectPanel) {
      const cdkOverlayPane = matSelectPanel.closest(
        '.cdk-overlay-pane',
      ) as HTMLElement;
      if (cdkOverlayPane) {
        setTimeout(() => {
          const bounds = cdkOverlayPane.getBoundingClientRect();
          if (window.innerHeight < bounds.height + bounds.top) {
            cdkOverlayPane.style.bottom =
              window.innerHeight - bounds.top + 25 + 'px';
            cdkOverlayPane.style.top = 'unset';
          }
        });
      }
    }
  }

  static getValidators(
    uiControlValidators: UIControlValidators,
  ): ValidatorFn[] {
    const validators = [];
    if (uiControlValidators) {
      for (let [key, value] of Object.entries(uiControlValidators)) {
        if (key === DynamicFormValidators.Required) {
          validators.push(Validators.required);
        } else if (key === DynamicFormValidators.Min && value != undefined) {
          validators.push(Validators.min(value as number));
        } else if (key === DynamicFormValidators.Max && value != undefined) {
          validators.push(Validators.max(value as number));
        } else if (
          key === DynamicFormValidators.Pattern &&
          value != undefined
        ) {
          validators.push(Validators.pattern(value));
        } else if (
          key === DynamicFormValidators.MaxLength &&
          value != undefined
        ) {
          validators.push(Validators.maxLength(value));
        } else if (
          key === DynamicFormValidators.MinLength &&
          value != undefined
        ) {
          validators.push(Validators.minLength(value));
        }
      }
    }
    return validators;
  }

  static getSelectedValue(
    formGroupOrControl: FormGroup | FormControl,
    config: Config,
  ) {
    let selectedValues = [];
    const selectedValue =
      formGroupOrControl instanceof FormControl
        ? formGroupOrControl?.value
        : formGroupOrControl?.get(config.property)?.value;
    if (
      selectedValue != null &&
      config.uiControlType === UiControlType.Dropdown
    ) {
      if (!config.dropdownID) {
        selectedValues = config.isMultiSelect ? selectedValue : [selectedValue];
      } else {
        const selectedIds = config.isMultiSelect
          ? selectedValue
          : [selectedValue];
        selectedValues = (config.values || [])
          .filter((val) =>
            selectedIds.some((selectedId) =>
              isEqual(selectedId, val[config.dropdownID]),
            ),
          )
          .map((val) => val[config.dropdownLabel]);
      }
    } else if (
      selectedValue != null &&
      config.uiControlType !== UiControlType.Dropdown
    ) {
      selectedValues = [selectedValue];
    }
    return selectedValues;
  }

  generateUniqueConfigName(baseName: string, existingConfigs: Array<string>) {
    // First, check if the exact base name is already taken
    if (!existingConfigs.includes(baseName)) {
      return baseName;
    }
    const regex = new RegExp(`^${baseName}(?:_(\\d+))?$`);
    const matching = existingConfigs
      .map((name) => {
        const match = name?.match(regex);
        if (match) {
          return match[1] ? parseInt(match[1], 10) : 0;
        }
        return null;
      })
      .filter((n) => n !== null) as number[];

    if (matching.length === 0) {
      return baseName;
    }

    const nextSuffix = Math.max(...matching) + 1;
    return `${baseName}_${nextSuffix}`;
  }

  stripSuffix(configName: string): string {
    return configName.replace(/_\d+$/, '');
  }

  /**
   * @description Parses a string value into a number, handling common formatting.
   * @param {any} value
   * @returns {number}
   */
  parseValue(value) {
    return +String(value).replace(/[$,%]/g, '').replace(/,/g, '');
  }

  /**
   * Returns a style object for a heatmap cell based on its value.
   * Applies normal or reversed color gradients depending on `reverseColorCoding`.
   */
  getCellStyle(
    column: string,
    value: any,
    preColoredStats: { [key: string]: any },
    reverseColorCoding: boolean,
  ): CellStyleParameters | {} {
    const stats = preColoredStats[column];
    if (!stats || value == null || value === '') return {};

    const num = this.parseValue(value);
    if (isNaN(num)) return {};

    const sortedValues: number[] = stats.sorted
      .filter((v) => v != null)
      .map((v) => Number(v));
    if (!sortedValues.length) return {};

    const min = Math.min(...sortedValues);
    const max = Math.max(...sortedValues);

    if (min === max) {
      // all values identical → use middle color
      return { 'background-color': '#FFEB84', color: '#000' };
    }

    // dynamic middle: median value like Excel
    const midIndex = Math.floor(sortedValues.length / 2);
    const mid = sortedValues[midIndex];

    // assign colors
    let startColor: string, midColor: string, endColor: string;
    if (!reverseColorCoding) {
      startColor = '#F8696B'; // red
      midColor = '#FFEB84'; // yellow
      endColor = '#63BE7B'; // green
    } else {
      startColor = '#63BE7B'; // green
      midColor = '#FFEB84'; // yellow
      endColor = '#F8696B'; // red
    }

    let color: string;
    if (num <= mid) {
      const denom = mid - min || 1; // avoid division by 0
      const factor = (num - min) / denom;
      color = this.mixColors(startColor, midColor, factor);
    } else {
      const denom = max - mid || 1;
      const factor = (num - mid) / denom;
      color = this.mixColors(midColor, endColor, factor);
    }

    return { 'background-color': color, color: '#000' };
  }

  /**
   * Produces a blended color between two hex colors.
   * Used by the table data to smoothly interpolate between
   * start and end colors based on a 0–1 factor.
   */
  mixColors(c1: string, c2: string, t: number): string {
    const toRGB = (hex: string) =>
      hex.match(/\w\w/g)!.map((x) => parseInt(x, 16));
    const c1RGB = toRGB(c1);
    const c2RGB = toRGB(c2);
    const rgb = c1RGB.map((c, i) => Math.round(c + (c2RGB[i] - c) * t));
    return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
  }

  /**
   * @description Parses an array of string values into numbers, handling common formatting.
   * @param {string[]} values
   * @returns {numbers[]}
   */
  parseFormattedNumbers(values: string[]): number[] {
    return values.map(
      (v) => Number(String(v).replace(/[^\d.-]/g, '')), // remove commas, spaces, etc.
    );
  }

  /**
   * @description Recursively searches an object for a given key and returns its value.
   * @param {any} obj - The object to search within.
   * @param {string} targetKey - The key name to find.
   * @returns {any} The value of the first matching key, or undefined if not found.
   */
  findValueByKey(obj: any, targetKey: string): any {
    if (!obj || typeof obj !== 'object') return undefined;

    for (const key in obj) {
      if (key === targetKey) {
        return obj[key];
      }
      const val = this.findValueByKey(obj[key], targetKey);
      if (val !== undefined) return val;
    }

    return undefined;
  }
}
